/* assets/tile-calculator.js — MisterTile calculator (prod, zonder debug)
 * - Selector ↔ calculator 2-richting, >10 OK, stapgrootte 1.
 * - Capture op plus/min/↑/↓ om themaclamp te omzeilen.
 * - Hook op input.value zodat programmatic wijzigingen óók syncen.
 * - areaFromBoxesIntervalSafe respecteert inclusieve bovengrens.
 * - UI: m² overlay toont GEEN decimalen (weergave = floor).
 */
(() => {
  /* -------------------- UTIL -------------------- */
  const toNum = (v, fb = 0) => {
    if (v == null) return fb;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : fb;
  };
  const EPS = 1e-6;
  const ceilToStep  = (val, step) => {
    const s = toNum(step, 1);
    if (!isFinite(s) || s <= 0) return Math.ceil(val);
    return Math.ceil(val / s) * s;
  };
  const floorToStep = (val, step) => {
    const s = toNum(step, 1);
    if (!isFinite(s) || s <= 0) return Math.floor(val);
    return Math.floor(val / s) * s;
  };
  const ceilBoxes = (area, wastePct, m2PerBox) => {
    const eff = area * (1 + wastePct / 100);
    return Math.max(1, Math.ceil(eff / Math.max(m2PerBox, 0.000001)));
  };

  // Inclusieve bovengrens respecteren (geen terugval)
  function areaFromBoxesIntervalSafe(B, wastePct, m2PerBox, stepStr, minArea) {
    const w = 1 + wastePct / 100;
    const lower = ((B - 1) * m2PerBox) / w; // exclusieve ondergrens
    const upper = (B * m2PerBox) / w;       // inclusieve bovengrens
    let candidate = Math.max(minArea, lower + EPS);
    candidate = ceilToStep(candidate, stepStr);
    if (candidate > upper) {
      candidate = floorToStep(upper, stepStr);
      candidate = Math.max(minArea, candidate);
      if (candidate <= lower + EPS) {
        candidate = Math.min(upper, ceilToStep(lower + 2 * EPS, stepStr));
      }
    }
    return candidate;
  }

  const getSectionEl = (sectionId, root) =>
    document.getElementById(`shopify-section-${sectionId}`) ||
    root.closest('[id^="shopify-section-"]') ||
    root.closest('.shopify-section') ||
    document;

  const findNativeQtyInputs = (sectionEl, calcRoot) =>
    Array.from(sectionEl.querySelectorAll('.quantity__input[name="quantity"]'))
      .filter((el) => !calcRoot.contains(el));

  const sectionWantsHide = (sectionEl) =>
    Array.from(sectionEl.querySelectorAll('.tile-calculator'))
      .some((calc) => (calc.dataset.hideNative || '').toLowerCase() === 'true');

  const getProductForm = (sectionEl) => sectionEl.querySelector('.product-form');

  const setNativeQtyVisibility = (sectionEl, pf, shouldHide) => {
    sectionEl.classList.toggle('mt-hide-native-qty', shouldHide);
    if (pf) pf.classList.toggle('tile-calculator--hide-native', shouldHide);
    const blocks = sectionEl.querySelectorAll(
      '.product-form__quantity, .product-form__input--quantity, [id^="Quantity-Form-"]'
    );
    blocks.forEach((el) => {
      if (shouldHide) { el.setAttribute('hidden',''); if (getComputedStyle(el).display!=='none') el.style.display='none'; }
      else { el.removeAttribute('hidden'); el.style.removeProperty('display'); }
    });
  };

  /* -------------------- CORE: take over selector + hook value -------------------- */
  function armSelectorCaptureHandlers(inputEl, onChange) {
    if (!inputEl || inputEl.__mtArmed) return;
    inputEl.__mtArmed = true;

    // Verwijder zichtbare clamps & zet dataset hoog (fallback)
    const HIGH_MAX = 1_000_000;
    inputEl.removeAttribute('max');
    inputEl.max = '';
    inputEl.removeAttribute('aria-valuemax');
    const wrapEl = inputEl.closest('quantity-input');
    if (wrapEl) {
      wrapEl.dataset.max = String(HIGH_MAX);
      wrapEl.dataset.quantityMax = String(HIGH_MAX);
      wrapEl.dataset.quantityAvailable = String(HIGH_MAX);
    }

    const qWrap = inputEl.closest('quantity-input, .quantity');
    const plus  = qWrap?.querySelector('.quantity__button[name="plus"]');
    const minus = qWrap?.querySelector('.quantity__button[name="minus"]');

    const stepOnce = (dir) => {
      const before = Math.max(1, Math.round(toNum(inputEl.value, 1)));
      const next   = Math.max(1, before + (dir > 0 ? 1 : -1));
      if (String(next) !== String(inputEl.value)) inputEl.value = String(next);
      onChange();
    };

    const captureClick = (dir) => (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation(); // blokkeer thema-±1/clamp
      stepOnce(dir);
    };

    plus  && plus.addEventListener('click',  captureClick(+1), { capture: true });
    minus && minus.addEventListener('click',  captureClick(-1), { capture: true });

    inputEl.addEventListener('keydown', (ev) => {
      if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return;
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      stepOnce(ev.key === 'ArrowUp' ? +1 : -1);
    }, { capture: true });

    // Typen/wheel/blur: laat door, maar sync alsnog
    ['input','change','blur'].forEach(ev =>
      inputEl.addEventListener(ev, () => onChange())
    );
  }

  function hookValueSetter(inputEl, onChange){
    if (!inputEl || inputEl.__mtValueHooked) return;
    const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (!desc || !desc.get || !desc.set) return;
    Object.defineProperty(inputEl, 'value', {
      configurable: true,
      enumerable: desc.enumerable,
      get(){ return desc.get.call(this); },
      set(v){
        const before = desc.get.call(this);
        desc.set.call(this, v);
        if (String(before) !== String(v)) Promise.resolve().then(onChange);
      }
    });
    inputEl.__mtValueHooked = true;
  }

  /* -------------------- CALCULATOR -------------------- */
  function initCalculator(root) {
    if (!root || root.__tcBound) return;
    root.__tcBound = true;

    const ds = root.dataset;
    const sectionEl = getSectionEl(ds.sectionId || '', root);

    const m2PerBox    = Math.max(toNum(ds.m2PerBox, 1), 0.000001);
    const minArea     = Math.max(1, toNum(ds.minArea, 1));
    const defArea     = Math.max(minArea, toNum(ds.defArea, minArea));
    const minWaste    = Math.max(0, toNum(ds.minWaste, 10));
    const defWaste    = Math.max(minWaste, toNum(ds.defWaste, minWaste));
    const singularLbl = ds.resultSingular || 'doos';
    const pluralLbl   = ds.resultPlural   || ds.resultLabel || 'dozen';

    const tilesPerBox = Math.max(0, parseInt(ds.tilesPerBox || '0', 10) || 0);
    const currency    = ds.currency || 'EUR';
    const pricePerBoxCents = Math.max(0, Math.round(toNum(ds.pricePerBox, 0)));
    const tileSingular= ds.tileSingular || 'tegel';
    const tilePlural  = ds.tilePlural   || 'tegels';

    const areaInput   = root.querySelector('[data-role="area"]');
    const wasteInput  = root.querySelector('[data-role="waste"]');
    const resultEl    = root.querySelector('[data-role="result"]');
    const totalsEl    = root.querySelector('[data-role="totals"]');

    if (areaInput) {
      areaInput.min = String(minArea);
      areaInput.step = areaInput.step || '1';
      if (!areaInput.value) areaInput.value = String(defArea);
    }
    if (wasteInput) {
      wasteInput.min = String(minWaste);
      wasteInput.step = wasteInput.step || '1';
      if (!wasteInput.value) wasteInput.value = String(defWaste);
    }

    let isPushingNative = false; // m² → dozen → selector
    let isPullingBack   = false; // selector → m²

    // € zonder spatie (ook NBSP verwijderen)
    const formatCurrencyCents = (cents) => {
      try {
        const s = new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(cents/100);
        return s.replace(/\u00A0/g, '').replace(/ /g, ''); // €154,44
      } catch(_) {
        return '€' + (cents/100).toFixed(2).replace('.', ',');
      }
    };
    const fmtNum = (n, fd=2) => new Intl.NumberFormat('nl-NL', { minimumFractionDigits: fd, maximumFractionDigits: fd }).format(n);
    const fmtInt = (n) => new Intl.NumberFormat('nl-NL').format(n);

    function updateTotals(boxes){
      if (!totalsEl) return;
      const totalPrice = boxes * pricePerBoxCents;
      const totalM2    = boxes * m2PerBox;
      const perBoxM2   = m2PerBox;
      const totalTiles = tilesPerBox > 0 ? boxes * tilesPerBox : 0;

      const priceStr   = formatCurrencyCents(totalPrice);
      const m2Digits   = Math.abs(totalM2 - Math.round(totalM2)) < 1e-9 ? 0 : 2; // hele m² → 0 decimalen
      const m2Str      = fmtNum(totalM2, m2Digits);
      const perBoxStr  = fmtNum(perBoxM2, (m2PerBox % 1 === 0) ? 0 : 2);
      const boxesLbl   = (boxes === 1 ? singularLbl : pluralLbl);

      // "= X tegel(s)" alleen tonen indien tilesPerBox bekend
      const tilesSuffix = totalTiles > 0 ? ` = ${fmtInt(totalTiles)} ${(totalTiles === 1 ? tileSingular : tilePlural)}` : '';

      // Alleen deze regel is aangepast: 2 regels via HTML met spans.
      const line1 = `${priceStr} voor ${m2Str} m²`;
      const line2 = `${boxes} ${boxesLbl} à ${perBoxStr} m²${tilesSuffix}`;
      totalsEl.innerHTML =
        `<span>${line1}</span><span>${line2}</span>`;
    }

    /* ---------- Forward: m²/waste → dozen (en selector) ---------- */
    function updateForward() {
      const area  = Math.max(minArea,  toNum(areaInput?.value,  defArea));
      const waste = Math.max(minWaste, Math.round(toNum(wasteInput?.value, defWaste)));
      const boxes = Math.max(1, ceilBoxes(area, waste, m2PerBox));

      if (resultEl) {
        const label = (boxes === 1 ? singularLbl : pluralLbl);
        resultEl.textContent = `${boxes} ${label}`;
      }
      updateTotals(boxes);

      if (isPullingBack) return;

      const qtyInputs = findNativeQtyInputs(sectionEl, root);
      isPushingNative = true;
      qtyInputs.forEach((inp) => {
        if (String(inp.value) !== String(boxes)) {
          inp.value = String(boxes);
          inp.dispatchEvent(new Event('input',  { bubbles: true }));
          inp.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      isPushingNative = false;
    }

    /* ---------- Reverse: selector → m² ---------- */
    function bindNativeQty(){
      const natives = findNativeQtyInputs(sectionEl, root);
      natives.forEach((inp) => {
        if (inp.__mtNativeBound) return;
        inp.__mtNativeBound = true;

        // Onschadelijk maken van max/clamps (fallback)
        inp.removeAttribute('max');
        inp.max = '';
        inp.removeAttribute('aria-valuemax');

        const onNativeChange = () => {
          if (isPushingNative) return;
          const boxesFromNative = Math.max(1, Math.round(toNum(inp.value, 1)));

          const currentArea  = Math.max(minArea,  toNum(areaInput?.value,  defArea));
          const currentWaste = Math.max(minWaste, Math.round(toNum(wasteInput?.value, defWaste)));
          const boxesNow     = ceilBoxes(currentArea, currentWaste, m2PerBox);
          if (boxesFromNative === boxesNow) {
            updateTotals(boxesFromNative);
            return;
          }

          const areaVal = areaFromBoxesIntervalSafe(
            boxesFromNative, currentWaste, m2PerBox, areaInput?.step || '1', minArea
          );

          isPullingBack = true;
          if (areaInput) {
            areaInput.value = String(areaVal);
            areaInput.dispatchEvent(new Event('input',  { bubbles: true }));
            areaInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          isPullingBack = false;

          updateForward();
        };

        // 1) Neem knoppen/keyboard over in CAPTURE (voorkomt thema-clamp & dubbele stappen)
        armSelectorCaptureHandlers(inp, onNativeChange);

        // 2) Hook de value-setter: ook programmatic updates van thema → reverse-sync
        hookValueSetter(inp, onNativeChange);

        // 3) Thema-events (als die alsnog komen) blijven óók werken
        ['input','change'].forEach(ev => inp.addEventListener(ev, onNativeChange));
      });
    }

    function applyHide(){
      setNativeQtyVisibility(sectionEl, getProductForm(sectionEl), sectionWantsHide(sectionEl));
    }

    // Listeners voor calculator velden
    ['input','change'].forEach(evt => {
      areaInput?.addEventListener(evt, updateForward);
      wasteInput?.addEventListener(evt, updateForward);
    });

    // Overlay-units voor calculator inputs (m² zonder decimalen — floor)
    function bindOverlayFor(qtyEl){
      if (!qtyEl || qtyEl.__mtOverlayBound) return;
      qtyEl.__mtOverlayBound = true;
      const wrap=qtyEl.querySelector('.qty-field'); const input=wrap?.querySelector('.quantity__input');
      if (!wrap || !input) return;
      let disp=wrap.querySelector('.qty-display');
      if (!disp){ disp=document.createElement('span'); disp.className='qty-display'; wrap.appendChild(disp); }
      const render=()=>{ 
        const raw = toNum(input.value, 0);
        if (qtyEl.dataset.role==='waste-qty') {
          disp.textContent = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 }).format(raw) + '%';
        } else {
          const shown = Math.floor(raw);
          disp.textContent = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 }).format(shown) + ' m²';
        }
      };
      ['input','change'].forEach(ev=>input.addEventListener(ev,render));
      ['minus','plus'].forEach(name=> qtyEl.querySelector(`.quantity__button[name="${name}"]`)?.addEventListener('click',()=>requestAnimationFrame(render)));
      render();
    }
    root.querySelectorAll('.quantity.has-unit').forEach(bindOverlayFor);

    // Eerste run
    bindNativeQty();
    applyHide();
    updateForward();

    // DOM-mutaties (variantwissels/section reload)
    const mo = new MutationObserver(() => {
      root.querySelectorAll('.quantity.has-unit').forEach(bindOverlayFor);
      bindNativeQty();
      applyHide();
    });
    mo.observe(sectionEl, { childList: true, subtree: true });
  }

  /* -------------------- BOOT -------------------- */
  function boot(){ document.querySelectorAll('.tile-calculator').forEach(initCalculator); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();